import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import ContactsRoundedIcon from '@mui/icons-material/ContactsRounded';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  applicationsApi,
  type ApplicationDeviceRiskResponseDto,
  type ApplicationOnboardingSignalsDto,
  type DeviceIdentificationAssessmentDto,
  type VisitorApplicationHistoryDto,
  type VisitorContactUsageDto,
} from '../../api/admin';
import { extractIpGeoFromAssessments } from '../../lib/fingerprintGeo';
import { declineReasonPlain, loanStatusPlain } from '../../lib/applicationJourneyCopy';
import { buildDeviceTabNarrative } from '../../lib/applicationTabNarratives';
import { ApplicationSectionCard } from './applicationSectionChrome';
import { IpLocationMap } from './IpLocationMap';
import { TabWhatsHappeningBanner } from './TabWhatsHappeningBanner';

function productTypePlain(productType: string): string {
  const t = productType.trim().toUpperCase();
  if (t === 'INSTALLMENT_LOAN') return 'Installment loan';
  if (t === 'PAYDAY' || t === 'PAYDAY_LOAN') return 'Payday loan';
  return productType.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function visitorAppOverview(row: VisitorApplicationHistoryDto): {
  title: string;
  body: string;
  tone: 'error' | 'warning' | 'success' | 'info';
} {
  const status = String(row.status ?? '').toUpperCase();
  const decline = declineReasonPlain(row.declineReason);

  if (status === 'DECLINED' || decline) {
    return {
      title: 'Declined',
      body: decline ?? loanStatusPlain('DECLINED'),
      tone: 'error',
    };
  }
  if (status === 'FUNDED') {
    return { title: 'Funded', body: loanStatusPlain(status), tone: 'success' };
  }
  if (status === 'APPROVED') {
    return { title: 'Approved', body: loanStatusPlain(status), tone: 'success' };
  }
  if (status === 'WITHDRAWN') {
    return { title: 'Withdrawn', body: loanStatusPlain(status), tone: 'warning' };
  }
  if (row.deviceRiskReview) {
    return {
      title: 'Needs review',
      body: 'Flagged for a closer look on device risk.',
      tone: 'warning',
    };
  }
  return {
    title: 'In progress',
    body: loanStatusPlain(status || 'ACTIVE'),
    tone: 'info',
  };
}

function ApproximateLocationContent({
  profile,
  assessments,
}: {
  profile: ApplicationDeviceRiskResponseDto['visitorProfile'];
  assessments: DeviceIdentificationAssessmentDto[];
}) {
  const ipGeo = useMemo(() => extractIpGeoFromAssessments(assessments), [assessments]);
  const countryLabel = profile?.ipCountry?.trim() || null;

  if (!ipGeo) {
    return (
      <Typography variant="body2" color="text.secondary">
        {profile
          ? 'No map pin yet — coordinates were not stored for this connection.'
          : 'No approximate location yet.'}
      </Typography>
    );
  }

  return (
    <IpLocationMap
      lat={ipGeo.lat}
      lng={ipGeo.lng}
      height={200}
      title={countryLabel ? `Approximate location · ${countryLabel}` : 'Approximate location'}
      subtitle={countryLabel ? `Country: ${countryLabel}` : undefined}
    />
  );
}

function VisitorHistorySection({ data }: { data: ApplicationDeviceRiskResponseDto }) {
  const theme = useTheme();
  const history = data.visitorApplicationHistory ?? [];

  if (history.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        First time this device is linked to an application.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      {history.map((row) => {
        const overview = visitorAppOverview(row);
        const chipColor =
          overview.tone === 'error'
            ? 'error'
            : overview.tone === 'warning'
              ? 'warning'
              : overview.tone === 'success'
                ? 'success'
                : 'default';

        return (
          <Box
            key={row.applicationId}
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: row.isCurrent ? 'primary.main' : 'divider',
              bgcolor: row.isCurrent ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                {row.isCurrent ? <Chip size="small" color="primary" label="This application" sx={{ height: 22 }} /> : null}
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {productTypePlain(row.productType)}
                </Typography>
              </Stack>
              <Chip
                size="small"
                color={chipColor}
                variant="outlined"
                label={overview.title}
                sx={{ fontWeight: 700, flexShrink: 0, height: 22 }}
              />
            </Stack>

            <Link
              component={RouterLink}
              to={`/applications/${encodeURIComponent(row.applicationId)}?tab=overview`}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              underline="hover"
              sx={{
                fontWeight: 600,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                display: 'inline-flex',
                alignItems: 'flex-start',
                gap: 0.4,
                wordBreak: 'break-all',
                lineHeight: 1.4,
                textDecorationLine: 'none',
                '&:hover': { textDecorationLine: 'underline' },
              }}
            >
              <Box component="span">{row.applicationId}</Box>
              <OpenInNewRoundedIcon sx={{ fontSize: 12, mt: 0.2, flexShrink: 0 }} />
            </Link>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.45 }}>
              {overview.body}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

function ContactUsageRow({ row }: { row: VisitorContactUsageDto }) {
  const priorOnly = row.seenOnApplications.filter((a) => !a.isCurrent);

  return (
    <Box
      sx={(t) => ({
        py: 0.75,
        px: 1,
        borderRadius: 1.25,
        border: '1px solid',
        borderColor: row.needsReview ? t.palette.warning.light : 'divider',
        bgcolor: row.needsReview ? alpha(t.palette.warning.main, 0.05) : 'transparent',
      })}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all', flex: 1 }}>
          {row.value}
        </Typography>
        {row.needsReview ? <Chip size="small" color="warning" variant="outlined" label="Review" sx={{ height: 20 }} /> : null}
      </Stack>
      {priorOnly.length > 0 ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
          Seen on {priorOnly.length} prior application{priorOnly.length === 1 ? '' : 's'}
          {priorOnly[0]?.status ? ` · latest ${String(priorOnly[0].status).replace(/_/g, ' ')}` : ''}
        </Typography>
      ) : null}
    </Box>
  );
}

function OtherContactsSection({ onboarding }: { onboarding: ApplicationOnboardingSignalsDto | null | undefined }) {
  if (!onboarding || (onboarding.priorApplicationCount ?? 0) === 0) return null;

  const otherEmails = onboarding.visitorContactInventory.emails.filter((row) => !row.usedOnCurrentApplication);
  const otherPhones = onboarding.visitorContactInventory.phones.filter((row) => !row.usedOnCurrentApplication);

  return (
    <ApplicationSectionCard
      title="Other contacts on this device"
      subtitle={
        otherEmails.length === 0 && otherPhones.length === 0
          ? 'Prior applications used the same email and phone.'
          : 'Different email or phone values from prior applications on this device.'
      }
      icon={<ContactsRoundedIcon fontSize="small" />}
    >
      {otherEmails.length === 0 && otherPhones.length === 0 ? null : (
        <Stack spacing={1.5}>
          {otherEmails.length > 0 ? (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.04em' }}>
                EMAILS
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {otherEmails.map((row) => (
                  <ContactUsageRow key={`email:${row.value}`} row={row} />
                ))}
              </Stack>
            </Box>
          ) : null}
          {otherPhones.length > 0 ? (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.04em' }}>
                PHONES
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {otherPhones.map((row) => (
                  <ContactUsageRow key={`phone:${row.value}`} row={row} />
                ))}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      )}
    </ApplicationSectionCard>
  );
}

export function ApplicationDeviceIntelligenceTab({ applicationId }: { applicationId: string }) {
  const q = useQuery({
    queryKey: ['applicationDeviceRisk', applicationId],
    queryFn: () => applicationsApi.deviceRisk(applicationId),
    enabled: !!applicationId,
    retry: false,
  });

  if (q.isPending) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (q.isError || !q.data) {
    return <Alert severity="info">Device intelligence data is not available for this application.</Alert>;
  }

  const data = q.data;
  const historyCount = data.visitorApplicationHistory?.length ?? 0;
  const narrative = buildDeviceTabNarrative(data);

  return (
    <Stack spacing={2}>
      <TabWhatsHappeningBanner narrative={narrative} rich />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 320px) minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <ApplicationSectionCard
          title="Approximate location"
          subtitle="Fingerprint connection pin"
          icon={<PlaceRoundedIcon fontSize="small" />}
        >
          <ApproximateLocationContent profile={data.visitorProfile} assessments={data.assessments} />
        </ApplicationSectionCard>

        <ApplicationSectionCard
          title={`Applications on this device (${historyCount})`}
          subtitle="What happened on each linked loan"
          icon={<DevicesRoundedIcon fontSize="small" />}
        >
          <VisitorHistorySection data={data} />
        </ApplicationSectionCard>
      </Box>

      <OtherContactsSection onboarding={data.onboarding} />
    </Stack>
  );
}
